import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import {
  AxiosError,
} from 'axios';
import {
  CourseEntity,
  ClassEntity,
  VseApplicationEntity,
  VseApplFormDocumentUploadHandle,
  VseApplFormDocumentUploadConfig,
} from 'vse-material-ui';
import {
  NewVseApplicationToken,
  NewVseApplDocumentState,
  NewVseApplicationState,
} from './newApplTypes';
import { useTypedSelector } from '../../store';
import * as VseAlert from '../alert';
import * as VseSession from '../session';

const initialState = {
  loading: 'idle',
  filteredCourses: [],
  courseFilterParam: {},
  documentStates: [ //TODO: Config dynamically
    {
      loading: 'idle',
      handle: {
        config: {
          documentType: 'HOMETSCPT',
          title: "Home University Transcript",
          required: true,
          accept: ["image/jpeg", "image/png", "application/pdf"],
        } as VseApplFormDocumentUploadConfig,
      } as VseApplFormDocumentUploadHandle,
    } as NewVseApplDocumentState,
    {
      loading: 'idle',
      handle: {
        config: {
          documentType: 'LANGPROF',
          title: "Proof of Language Proficiency",
          required: false,
          accept: ["image/jpeg", "image/png", "application/pdf"],
        } as VseApplFormDocumentUploadConfig,
      } as VseApplFormDocumentUploadHandle,
    } as NewVseApplDocumentState,
  ],
} as NewVseApplicationState;

const initialNewAppl = {
  applicationID: 0,
  seasonID: 0,
  referenceNumber: '',
  userName: '',
  emailAddress: '',
  homeUniversityID: 0,
  homeUniversityStudentNumber: '',
  familyName: '',
  firstName: '',
  middleName: '',
  studyLevel: '',
  major: '',
  studyYear: '',
  submissionDate: '',
  status: '',
  classes: [],
  documents: [],
} as VseApplicationEntity;

function transformResponse(data: any) {
  try {
    let j = JSON.parse(data, (key, value) => {
      switch (key) {
        case 'startDate':
        case 'endDate':
        case 'outgoingApplicationStartDate':
        case 'outgoingApplicationDeadline':
          return new Date(value);
        default:
          return value;
      }
    });
    return j;
  } catch {
    // Not json
    return data;
  }
}

let sources: Array<{ requestId: string, source: CancelTokenSource }> = [];

export const prepare = createAsyncThunk('newAppl/prepare', async (seasonID: number, thunkApi) => {
  try {
    const response = await axios.get<NewVseApplicationToken>(`api/apply/seasons/${seasonID}`, {
      transformResponse: transformResponse
    });
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadCourses = createAsyncThunk('newAppl/loadCourses', async (seasonID: number, thunkApi) => {
  //TODO: filtering
  try {
    const response = await axios.get<CourseEntity[]>(`api/apply/seasons/${seasonID}/courses`, {
      transformResponse: transformResponse
    });
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadClasses = createAsyncThunk('newAppl/loadClasses', async (course: CourseEntity, thunkApi) => {
  try {
    const response = await axios.get<ClassEntity[]>(`api/seasons/${course.seasonID}/universities/${course.universityID}/terms/${encodeURIComponent(course.termCode.trimRight())}/courses/${encodeURIComponent(course.courseCode.trimRight())}/classes`);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const uploadDocument = createAsyncThunk('newAppl/uploadDocument', async ({
  file,
}: {
  handle: VseApplFormDocumentUploadHandle,
  file: File,
}, thunkApi) => {
  try {
    let formData = new FormData();
    formData.append("file", file);
    const source = axios.CancelToken.source();
    thunkApi.signal.addEventListener('abort', () => {
      source.cancel();
    });

    sources.push({ requestId: thunkApi.requestId, source: source });

    let config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      cancelToken: source.token,
    }
    const response = await axios.post<{ fileID: string }>(`api/file/upload`, formData, config);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const submitNewAppl = createAsyncThunk('newAppl/submitNewAppl', async (newAppl: VseApplicationEntity, thunkApi) => {
    try {
      const response = await axios.post<NewVseApplicationToken>(`api/apply/seasons/${newAppl.seasonID}`, newAppl);
      return response;
    } catch (err) {
      const error: AxiosError = err;
      if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
        VseAlert.handleThunkError(thunkApi, error);
      }
      return thunkApi.rejectWithValue(error);
    }
});

export const newApplSlice = createSlice({
  name: 'newAppl',
  initialState,
  reducers: {
    reset: (state) => {
      state = Object.assign(state, initialState);
    },
    addDocumentChanged: (state, action: PayloadAction<{
      documentType: string,
      file: File,
    }>) => {
      let docState = state.documentStates.find((s) => s.handle.config.documentType === action.payload.documentType);
      if (docState) {
        docState.handle.file = action.payload.file;
        docState.handle.fileName = action.payload.file.name;
        docState.handle.uploaded = false;
        docState.handle.uploading = false;
      }
    },
    removeDocument: (state, action: PayloadAction<{
      documentType: string,
      requestId?: string,
    }>) => {
      let docState = state.documentStates.find((s) => s.handle.config.documentType === action.payload.documentType);
      if (docState) {
        docState.loading = 'idle';
        docState.handle.file = undefined;
        docState.handle.requestId = undefined;
        docState.handle.fileID = undefined;
        docState.handle.fileName = undefined;
        docState.handle.contentType = undefined;
        docState.handle.uploaded = false;
        docState.handle.uploading = false;
      }
      const i = sources.findIndex((e) => e.requestId === action.payload.requestId);
      if (i >= 0) {
        sources[i].source.cancel();
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(VseSession.reset, (state) => {
      state = Object.assign(state, initialState);
    });
    builder.addCase(prepare.pending, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(prepare.fulfilled, (state, action) => {
      state.loading = 'ready';
      state.token = action.payload.data;
      state.token.application = { ...initialNewAppl, ...action.payload.data.application };
    });
    builder.addCase(prepare.rejected, (state) => {
      state.loading = 'failed';
      state.token = undefined;
    });
    builder.addCase(loadCourses.fulfilled, (state, action) => {
      state.filteredCourses = action.payload.data;
    });
    builder.addCase(loadClasses.fulfilled, (state, action) => {
      state.classesOfCourseSelected = action.payload.data;
    });
    builder.addCase(loadClasses.rejected, (state) => {
      state.classesOfCourseSelected = undefined;
    });
    builder.addCase(uploadDocument.pending, (state, action) => {
      let docState = state.documentStates.find((docState) => docState.handle.config.documentType === action.meta.arg.handle.config.documentType);
      if (docState) {
        docState.loading = 'pending';
        docState.handle.fileName = action.meta.arg.file.name;
        docState.handle.contentType = action.meta.arg.file.type;
        docState.handle.requestId = action.meta.requestId;
        docState.handle.uploading = true;
        docState.handle.uploaded = false;
      }
    });
    builder.addCase(uploadDocument.fulfilled, (state, action) => {
      let docState = state.documentStates.find((docState) => docState.handle.config.documentType === action.meta.arg.handle.config.documentType);
      if (docState) {
        docState.loading = 'succeeded';
        docState.handle.fileID = action.payload.data.fileID;
        docState.handle.uploading = false;
        docState.handle.uploaded = true;
        //TODO: to add to appl.vseappldocument
      }
      const i = sources.findIndex((e) => e.requestId === action.meta.requestId);
      if (i >= 0) {
        sources.splice(i, 1);
      }
    });
    builder.addCase(uploadDocument.rejected, (state, action) => {
      let docState = state.documentStates.find((docState) => docState.handle.config.documentType === action.meta.arg.handle.config.documentType);
      if (docState) {
        docState.loading = 'failed';
        docState.handle.fileID = undefined;
        docState.handle.fileName = undefined;
        docState.handle.requestId = undefined;
        docState.handle.uploading = false;
        docState.handle.uploaded = false;
      }
      const i = sources.findIndex((e) => e.requestId === action.meta.requestId);
      if (i >= 0) {
        sources.splice(i, 1);
      }
    });
    builder.addCase(submitNewAppl.pending, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(submitNewAppl.fulfilled, (state) => {
      state.loading = 'succeeded';
    });
    builder.addCase(submitNewAppl.rejected, (state, action) => {
      state.loading = 'ready';
      state.token!.application = action.meta.arg;
    });
  },
});

export const {
  removeDocument,
  reset,
} = newApplSlice.actions;

export default newApplSlice.reducer;

export const useNewVseApplicationState = (): NewVseApplicationState => useTypedSelector(state => state.newAppl);
